import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from 'components/v2/Tooltip';
import { useEdgeAvailability } from 'api/queries/edge';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isBefore,
  startOfDay,
  isSameMonth,
  addWeeks,
  getWeeksInMonth,
  addDays,
  parseISO,
} from 'date-fns';
import useDateRangeSelection from './useDateRangeSelection';

const CELL_SIZE = 32;
const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const weekHeaderGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '5px',
  justifyItems: 'center',
  alignItems: 'center',
  mb: 1,
};

const dayGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gridAutoRows: 'auto',
  gap: '8px 0',
  justifyItems: 'center',
  alignItems: 'center',
};

const cellOuter = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  py: '2px',
  position: 'relative',
};

const cellBase = {
  aspectRatio: '1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  lineHeight: 1,
  borderRadius: '50%',
  userSelect: 'none',
  width: '100%',
  maxWidth: CELL_SIZE,
  zIndex: 1,
};

const statusStyles = {
  available: {
    border: '1.334px solid',
    borderColor: 'darkSand.white40',
    backgroundColor: 'common.white',
    color: 'text.primary',
    cursor: 'pointer',
    '&:hover': { borderColor: 'secondary.main' },
  },
  onRequest: {
    border: '1.334px solid',
    borderColor: 'darkSand.white40',
    backgroundColor: 'common.white',
    color: 'text.primary',
    cursor: 'pointer',
    '&:hover': { borderColor: 'secondary.main' },
  },
  unavailable: {
    backgroundColor: 'rgba(0,0,0,0.12)',
    color: 'text.secondary',
  },
  loading: {
    backgroundColor: 'rgba(0,0,0,0.12)',
    color: 'text.secondary',
  },
  past: {
    backgroundColor: 'rgba(0,0,0,0.12)',
    color: 'text.secondary',
  },
  selected: {
    backgroundColor: 'secondary.main',
    color: 'vividOrange.black80',
    cursor: 'pointer',
    border: '1.334px solid',
    borderColor: 'secondary.main',
    fontWeight: 600,
  },
  selectedTween: {
    backgroundColor: 'vividOrange.white40',
    color: 'vividOrange.black80',
    cursor: 'pointer',
    border: '1.334px solid',
    borderColor: 'vividOrange.white40',
  },
  hoverPreview: {
    backgroundColor: 'vividOrange.white40',
    color: 'vividOrange.black80',
    cursor: 'pointer',
    border: '1.334px solid',
    borderColor: 'vividOrange.white40',
  },
  outOfMonth: {
    backgroundColor: 'rgba(0,0,0,0.06)',
    color: 'rgba(0,0,0,0.35)',
  },
};

function getTooltipText(status) {
  if (status === true) return 'Available';
  if (status === 'onRequest') return 'On Request';
  if (status === false) return 'Unavailable';
  return '';
}

export default function EdgeAvailabilityCalendar({
  savedItineraryId,
  selectedDate,
  onSelectDate,
  onRangeSelect,
  stayNights = 0,
  adults,
  childrenAges,
  initialMonth,
  skipAvailability = false,
}) {
  const currentDate = useMemo(() => initialMonth || new Date(), [initialMonth]);
  const [hoverDateSimple, setHoverDateSimple] = useState(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  const { data, isLoading: queryLoading } = useEdgeAvailability(savedItineraryId, currentDate, { adults, childrenAges });

  const isLoading = !skipAvailability && queryLoading;
  const availability = data?.availability || {};

  const { selectedDates, endDateString } = useMemo(() => {
    if (!selectedDate || stayNights <= 0)
      return { selectedDates: new Set(selectedDate ? [selectedDate] : []), endDateString: selectedDate };
    const start = parseISO(selectedDate);
    const end = addDays(start, stayNights);
    return {
      selectedDates: new Set(eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd'))),
      endDateString: format(end, 'yyyy-MM-dd'),
    };
  }, [selectedDate, stayNights]);

  const range = useDateRangeSelection({ selectedDate, stayNights, onRangeSelect });
  const hoverDate = onRangeSelect ? range.hoverDate : hoverDateSimple;
  const hoverDates = onRangeSelect ? range.hoverDates : new Set();
  const hoverBefore = onRangeSelect ? range.hoverBefore : false;

  const days = useMemo(() => {
    const start = startOfWeek(monthStart, { weekStartsOn: 1 });
    const weeks = getWeeksInMonth(currentDate, { weekStartsOn: 1 });
    const delta = 6 - weeks;
    const end = addWeeks(endOfWeek(monthEnd, { weekStartsOn: 1 }), delta);
    return eachDayOfInterval({ start, end });
  }, [currentDate, monthStart, monthEnd]);

  const today = startOfDay(new Date());

  return (
    <Box data-test-id="availability-calendar">
      {isLoading && (
        <Stack alignItems="center" sx={{ mb: 1 }}>
          <CircularProgress size={14} color="secondary" />
        </Stack>
      )}

      <Box sx={weekHeaderGridSx}>
        {DAYS.map((d, i) => (
          <Typography key={i} variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {d}
          </Typography>
        ))}
      </Box>

      <Box sx={dayGridSx}>
        {days.map(day => {
          const dateString = format(day, 'yyyy-MM-dd');
          const inMonth = isSameMonth(day, currentDate);
          const isPast = isBefore(day, today);
          const isSelected = selectedDates.has(dateString);
          const isHovered = hoverDates.has(dateString);
          const status = availability[dateString];
          const hasData = status !== undefined;

          const isClickableDate = skipAvailability
            ? !isPast
            : inMonth && !isPast && (status === true || status === 'onRequest' || dateString === selectedDate);

          const isEndDate = dateString === endDateString;
          const isStartDate = dateString === selectedDate;
          let cellStyle;
          if (isSelected && (isStartDate || isEndDate)) {
            cellStyle = statusStyles.selected;
          } else if (isSelected) {
            cellStyle = statusStyles.selectedTween;
          } else if (isHovered && isClickableDate) {
            cellStyle = statusStyles.hoverPreview;
          } else if (!inMonth) {
            cellStyle = skipAvailability && !isPast ? statusStyles.available : statusStyles.outOfMonth;
          } else if (isPast) {
            cellStyle = statusStyles.past;
          } else if (skipAvailability) {
            cellStyle = statusStyles.available;
          } else if (isLoading || !hasData) {
            cellStyle = statusStyles.loading;
          } else if (status === true) {
            cellStyle = statusStyles.available;
          } else if (status === 'onRequest') {
            cellStyle = statusStyles.onRequest;
          } else {
            cellStyle = statusStyles.unavailable;
          }

          const isClickable = isClickableDate || isStartDate;

          const circle = <Box sx={{ ...cellBase, ...cellStyle }}>{format(day, 'd')}</Box>;

          const inRange = isSelected && stayNights > 0;
          const inHoverRange = hoverDates.has(dateString);
          const showHighlight = inRange || inHoverRange;
          const isRangeStart = isStartDate && showHighlight;
          const isRangeEnd = isEndDate && inRange;
          const isHoverEnd = inHoverRange && dateString === hoverDate;

          let highlightSx = {};
          if (showHighlight) {
            const bg = inRange ? 'rgba(255,110,0,0.3)' : 'rgba(255,110,0,0.15)';
            const band = {
              content: '""',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              height: CELL_SIZE - 2,
              zIndex: 0,
            };
            const isAnchorStart = isRangeStart || (isStartDate && inHoverRange);
            const isAnchorEnd = isRangeEnd || isHoverEnd;
            if (isAnchorStart && isAnchorEnd) {
              highlightSx = {};
            } else if (isAnchorStart) {
              const side = inHoverRange && hoverBefore ? { left: 0, right: '50%' } : { left: '50%', right: 0 };
              highlightSx = { '&::after': { ...band, ...side, backgroundColor: bg } };
            } else if (isAnchorEnd) {
              const side = inHoverRange && hoverBefore ? { left: '50%', right: 0 } : { left: 0, right: '50%' };
              highlightSx = { '&::after': { ...band, ...side, backgroundColor: bg } };
            } else {
              highlightSx = { '&::after': { ...band, left: 0, right: 0, backgroundColor: bg } };
            }
          }

          const rangeHandlers = onRangeSelect ? range.getHandlers(dateString, isClickable) : {};

          const handleMouseEnter = isClickable
            ? () => {
                if (onRangeSelect) {
                  rangeHandlers.handleMouseEnter?.();
                } else {
                  setHoverDateSimple(dateString);
                }
              }
            : undefined;

          const handleClick = isClickable
            ? () => {
                if (onRangeSelect) {
                  rangeHandlers.handleClick?.();
                  return;
                }
                onSelectDate(isStartDate ? null : dateString);
              }
            : undefined;

          const cell = (
            <Box
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              aria-label={isClickable ? format(day, 'MMMM d, yyyy') : undefined}
              onClick={handleClick}
              onMouseDown={rangeHandlers.handleMouseDown}
              onMouseUp={rangeHandlers.handleMouseUp}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => (onRangeSelect ? range.getHandlers(dateString, false) : setHoverDateSimple(null))}
              onKeyDown={
                isClickable && !onRangeSelect ? e => e.key === 'Enter' && onSelectDate(isStartDate ? null : dateString) : undefined
              }
              sx={{ ...cellOuter, cursor: isClickable ? 'pointer' : 'default', ...highlightSx }}
            >
              {circle}
            </Box>
          );

          const tooltipText = inMonth && !isPast && hasData && !isLoading ? getTooltipText(status) : '';
          return (
            <Tooltip key={dateString} title={tooltipText}>
              {cell}
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
